import React, { useState, useRef, useEffect } from 'react';
import ConfirmModal from '../../modals/ConfirmModal';

const TextboxPhoto = ({ 
  photo, 
  onDelete, 
  onScaleUp, 
  onScaleDown, 
  onMoveUp, 
  onMoveDown,
  canMoveUp,
  canMoveDown,
  onHeightChange
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const imgRef = useRef(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, onConfirm: () => {} });
  
  // Report height changes
  useEffect(() => {
    if (imgRef.current && onHeightChange) {
      const mmToPx = 3.779527559;
      const heightInMm = imgRef.current.offsetHeight / mmToPx;
      onHeightChange(heightInMm + 5); // Add 5mm margin
    }
  }, [photo.scale, onHeightChange]);

  const handleDelete = () => {
    setConfirmModal({
      isOpen: true,
      onConfirm: () => {
        setConfirmModal({ isOpen: false, onConfirm: () => {} });
        onDelete();
      }
    });
  };

  return (
    <div 
      style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        marginBottom: '5mm',
        position: 'relative'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <img 
          ref={imgRef}
          src={photo.url} 
          alt={photo.name || 'Textbox photo'}
          style={{
            maxWidth: `${photo.scale || 100}%`,
            height: 'auto',
            display: 'block',
            border: '1px solid #e5e7eb',
            borderRadius: '4px'
          }}
          onLoad={() => {
            // Report height after image loads
            if (imgRef.current && onHeightChange) {
              const mmToPx = 3.779527559;
              const heightInMm = imgRef.current.offsetHeight / mmToPx;
              onHeightChange(heightInMm + 5); // Add 5mm margin
            }
          }}
        />
        
        {isHovered && (
          <div style={{
            position: 'absolute',
            top: '8px',
            left: '8px',
            display: 'flex',
            gap: '4px',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            padding: '4px',
            borderRadius: '4px'
          }}>
            <button
              onClick={onMoveUp}
              disabled={!canMoveUp}
              style={{
                width: '28px',
                height: '28px',
                border: 'none',
                borderRadius: '3px',
                backgroundColor: canMoveUp ? '#3b82f6' : '#6b7280',
                color: 'white',
                cursor: canMoveUp ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Move Up"
            >
              ↑
            </button>
            
            <button
              onClick={onMoveDown}
              disabled={!canMoveDown}
              style={{
                width: '28px',
                height: '28px',
                border: 'none',
                borderRadius: '3px',
                backgroundColor: canMoveDown ? '#3b82f6' : '#6b7280',
                color: 'white',
                cursor: canMoveDown ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Move Down"
            >
              ↓
            </button>
            
            <button
              onClick={onScaleDown}
              disabled={photo.scale <= 30}
              style={{
                width: '28px',
                height: '28px',
                border: 'none',
                borderRadius: '3px',
                backgroundColor: photo.scale <= 30 ? '#6b7280' : '#10b981',
                color: 'white',
                cursor: photo.scale <= 30 ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Scale Down (10%) - Min 30%"
            >
              −
            </button>
            
            <button
              onClick={onScaleUp}
              disabled={photo.scale >= 75}
              style={{
                width: '28px',
                height: '28px',
                border: 'none',
                borderRadius: '3px',
                backgroundColor: photo.scale >= 75 ? '#6b7280' : '#10b981',
                color: 'white',
                cursor: photo.scale >= 75 ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Scale Up (10%) - Max 75%"
            >
              +
            </button>
            
            <button
              onClick={handleDelete}
              style={{
                width: '28px',
                height: '28px',
                border: 'none',
                borderRadius: '3px',
                backgroundColor: '#ef4444',
                color: 'white',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Delete Photo"
            >
              ×
            </button>
          </div>
        )}
      </div>
      
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, onConfirm: () => {} })}
        onConfirm={confirmModal.onConfirm}
        title="Delete Photo"
        message="Are you sure you want to delete this photo? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

export default TextboxPhoto;
